import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import { DateFormat, formatDate } from '@/utils/dates';
import { useStore } from '@/store';

import { AuditEntity, AuditLogWithUser, AuditOperation } from '@extTypes/auditLog/types';
import { OptionSetDto } from '@extTypes/options/optionSet';
import { Settings } from '@extTypes/settings/settings';
import { EntityVersionRestored } from '@extTypes/versioning';

import { saveSettings } from '@/api/shopify/settings';
import { getAuditEntriesWithUsers } from '@/api/customily/auditLog';
import { getOptionSetFromVersion, listOptionSetVersions } from '@/api/customily/optionSet';
import { getSettingsFromVersion, listSettingsVersions } from '@/api/customily/settings';

import RestoreEntityVersion from '@/components/AuditLog/RestoreEntityVersion.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'AuditLog',
  props: {
    value: { type: Boolean },
    entityType: null,
    entityId: null,
    entityCreatedDate: null,
    displayName: null,
    isEntityIdClientId: { type: Boolean }
  },
  emits: ["input", "close", "on-version-restored"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'input', value: boolean): void;
    (event: 'close'): void;
    (event: 'on-version-restored', data: EntityVersionRestored): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    value: boolean;
    entityType: AuditEntity;
    entityId?: string | number;
    entityCreatedDate?: Date | string;
    displayName?: string;
    isEntityIdClientId?: boolean;
};



const listVersions = () => {
    if (props.entityType === AuditEntity.SETTING) {
        return (id: string | number) => listSettingsVersions(String(id));
    }
    if (props.entityType === AuditEntity.OPTION_SET) {
        return (id: string | number) => listOptionSetVersions(String(id));
    }
    throw new Error('Unsupported entity type');
};

const getVersion = () => {
    if (props.entityType === AuditEntity.SETTING) {
        return (key: string, versionId: string) => getSettingsFromVersion(key, versionId);
    }
    if (props.entityType === AuditEntity.OPTION_SET) {
        return (key: string, versionId: string) => getOptionSetFromVersion(key, versionId);
    }
    throw new Error('Unsupported entity type');
};

const restoreVersion = (): ((data: Settings.Settings | OptionSetDto, id: string | number) => Promise<void>) => {
    if (props.entityType === AuditEntity.SETTING) {
        return (async (data: Settings.Settings, id: string | number) => {
            await saveSettings(data, String(id), false);
        }) as (data: Settings.Settings | OptionSetDto, id: string | number) => Promise<void>;
    }
    if (props.entityType === AuditEntity.OPTION_SET) {
        // The actual restoration logic for OPTION_SET is handled in the parent component's
        // onOptionSetVersionRestored handler. This stub is intentionally left empty for type compatibility.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        return (async (data: OptionSetDto, id: string) => {
            return Promise.resolve();
        }) as (data: Settings.Settings | OptionSetDto, id: string | number) => Promise<void>;
    }
    throw new Error('Unsupported entity type');
};

const entityHandlesVersioning = computed(
    () => props.entityType === AuditEntity.SETTING || props.entityType === AuditEntity.OPTION_SET
);
const loading = ref(true);
const handlingEntityVersions = ref(false);
const auditEntries = ref<AuditLogWithUser[]>([]);
const { state } = useStore();



const closeModal = () => {
    handlingEntityVersions.value = false;
    emit('close');
};

const isOpen = computed({
    get(): boolean {
        return props.value;
    },
    set(value: boolean) {
        emit('input', value);
    }
});

const entityVersionRestored = (data: EntityVersionRestored) => {
    emit('on-version-restored', data);
    closeModal();
};

watch(
    () => props.entityId,
    () => {
        if (props.entityId || props.isEntityIdClientId) {
            getAuditEntriesWithUsers(
                props.entityType,
                `${props.isEntityIdClientId ? state.user?.client_id : props.entityId}`
            )
                .then(async (data) => {
                    if (!data.some((e) => e.operation == 'C') && props) {
                        data.unshift({
                            entity: props.entityType,
                            entityId: `${props.isEntityIdClientId ? state.user?.client_id : props.entityId}`,
                            operation: AuditOperation.CREATE,
                            performedByUserId: state.user?.customilyUser.Id!,
                            date: props.entityCreatedDate ? new Date(props.entityCreatedDate) : '',
                            impersonatedUserId: null,
                            action: 'null',
                            details: null,
                            user: {
                                Id: 0,
                                Name: state.user?.customilyUser.Name ?? '',
                                Username: state.user?.customilyUser.Username ?? ''
                            }
                        });
                    }

                    auditEntries.value = data.reverse();

                    loading.value = false;
                })
                .catch((error) => {
                    console.error('Error getting audit entries with users', JSON.stringify(error));
                    loading.value = false;
                });
        }
    },
    { immediate: true }
);

const parseOperation = (operation: AuditOperation): string => {
    switch (operation) {
        case AuditOperation.CREATE:
            return 'Created';
        case AuditOperation.RESTORE:
            return 'Restored';
        case AuditOperation.UPDATE:
            return 'Updated';
        case AuditOperation.DELETE:
            return 'Deleted';
        default:
            return 'Unknown';
    }
};

const parseEntity = (): string => {
    switch (props.entityType) {
        case AuditEntity.LIBRARY:
            return 'Library';
        case AuditEntity.OPTION_SET:
            return 'Option Set';
        case AuditEntity.PRODUCT:
            return 'Product';
        case AuditEntity.TEMPLATE:
            return 'Template';
        case AuditEntity.SETTING:
            return 'Setting';
        default:
            return 'Unknown';
    }
};

const getTitle = (): string => {
    return `${parseEntity()} history`;
};

const getFormattedDate = (date: Date | string): string => {
    if (date === '') return date;
    return formatDate(date, DateFormat.MMMddYYYYTime);
};

const handleHistory = () => {
    handlingEntityVersions.value = false;
};

const handleEntityVersions = () => {
    handlingEntityVersions.value = true;
};

return { __sfc: true,props, listVersions, getVersion, restoreVersion, entityHandlesVersioning, loading, handlingEntityVersions, auditEntries, state, emit, closeModal, isOpen, entityVersionRestored, parseOperation, parseEntity, getTitle, getFormattedDate, handleHistory, handleEntityVersions, RestoreEntityVersion }
}

})